import CustomInputs from '../../common/Inputs';
import { GetImages } from '../../../utils/GetImages';
import { useError } from '../../../utils/context/ErrorContext';
import { useOnboardingContext } from '../../../utils/context/OnboardingContext';
import { FORM_CONTROLFIELD_TYPES } from '../../../constants/constants';
import SimpleButton from '../../../newComponents/buttons/Simple-button';
import Checkbox from '../../../newComponents/form-fields/checkbox';
import DateHelper from '../../../utils/DateHelper';

export const SIGNATURE_NAME_MAX_LENGTH = 40;
export const SIGNATURE_MAX_LENGTH_MESSAGE = `Signature cannot exceed ${SIGNATURE_NAME_MAX_LENGTH} characters`;
export const SIGNATURE_LETTERS_ONLY_MESSAGE = "Signature can only contain letters";
const NON_LETTER_PATTERN = /[^a-zA-Z ]/g;

const SignatureCard = ({
  clause,
  formData,
  setFormData,
  editable,
  handleChange,
  isPreview = false,
  landlordEdit = undefined,
  handleClick = () => { },
  displayText = undefined,
  displayCheckbox = true,
  buttonName = "Sign Application",
  isLoading = false,
}) => {
  const { errors, setErrors, errorMessages, setErrorMessages } = useError()
  const { isSignatureConfirm, setIsSignatureConfirm } = useOnboardingContext();
  const { fullName, signedDateTime } = formData || {};
  const trimmedName = (fullName ?? "").trim();
  const isRole = trimmedName === "Landlord" || trimmedName === "Tenant";
  const isSignatureCompleted =
    !!trimmedName && !isRole && !!signedDateTime;
  const isEditableLivePath = !isPreview && editable;

  const handleSignatureNameChange = (e) => {
    const raw = e?.target?.value ?? "";
    const currentValue = isRole ? "" : (fullName ?? "");
    const setNameError = (message: string) => {
      setErrors((prev) => ({ ...prev, fullName: !!message }));
      setErrorMessages((prev) => ({ ...prev, fullName: message }));
    };
    const emit = (value: string) =>
      handleChange?.({
        ...e,
        target: { ...e.target, name: "fullName", value },
      });

    // Parents clear the fullName error inside handleChange, so the message must be
    // set after emitting or it gets wiped in the same batch.
    const lettersOnly = raw.replace(NON_LETTER_PATTERN, "");
    if (lettersOnly.length > SIGNATURE_NAME_MAX_LENGTH) {
      // Already full: reject the keystroke instead of slicing, which dropped the
      // last character when typing mid-text. Otherwise (e.g. paste) cap it.
      if (currentValue.length < SIGNATURE_NAME_MAX_LENGTH) {
        emit(lettersOnly.slice(0, SIGNATURE_NAME_MAX_LENGTH));
      }
      setNameError(SIGNATURE_MAX_LENGTH_MESSAGE);
      return;
    }
    if (lettersOnly !== raw) {
      emit(lettersOnly);
      setNameError(SIGNATURE_LETTERS_ONLY_MESSAGE);
      return;
    }
    setNameError("");
    handleChange?.(e);
  };

  const clauseText = clause?.trim() ? clause : null;

  return (
    <div id="signature-section" className={landlordEdit ? 'signature-card-input-landlord' : 'signature-card-input'}>
      {/* Template signature text is visible before and after signing (Tenant + Landlord portals). */}
      {clauseText ? (
        <div className="signature_checkbox p-3 normal_text black">{clauseText}</div>
      ) : null}

      {isEditableLivePath ? (
        <div className="signature-card">
          {displayCheckbox && (
            <div className='signature_checkbox px-3 py-2'>
              <Checkbox
                onChange={() => {
                  setIsSignatureConfirm(!isSignatureConfirm);
                  setErrors((prev) => ({ ...prev, isSignatureConfirm: false }));
                  setErrorMessages((prev) => ({ ...prev, isSignatureConfirm: "" }));
                }}
                isChecked={isSignatureConfirm}
                label="I confirm that the information provided above is true and based on my personal knowledge. I give my consent to share or use this information as needed." />
              {errors.isSignatureConfirm && (
                <p className="errorMessageStyles py-1 px-1">{errorMessages.isSignatureConfirm}</p>
              )}

            </div>
          )}
          <div className="authority_form signature-sign">
            <CustomInputs
              name="fullName"
              type={FORM_CONTROLFIELD_TYPES.TEXT}
              value={formData?.fullName == "Landlord" || formData?.fullName == "Tenant" ? "" : formData?.fullName}
              labelText={landlordEdit ? "Landlord Signature" : "Your Signature"}
              isFormControl={true}
              onChange={handleSignatureNameChange}
              // Allow one char past the cap so onChange can surface the max message (HTML maxLength alone was silent).
              maxLength={SIGNATURE_NAME_MAX_LENGTH + 1}
              placeholder={"Type your name"}
              myClassName={"containerTextField"}
              isError={errors.fullName}
              isRequired={true}
              requiredMessage="Full Name is required"
              errorMessages={errorMessages.fullName}
              width="260px"
            />

          </div>
          <div>
            <div className='p-2'>
              <SimpleButton type='button' label={buttonName} onClick={handleClick} isLoading={isLoading} />
            </div>
          </div>
        </div>
      ) : null}

      {/* Signed name / date / confirmation only when signature is completed */}
      {isSignatureCompleted && (
        <div>
          <div className="authority_form p-0">
            <div className="flexColumn">
              {displayText && (
                <div className='signature_checkbox p-3'>
                  I confirm that the information provided above is true and based on my personal knowledge. I give my consent to share or use this information as needed.
                </div>)}
              <div className='p-3 d-flex flex-column gap-2'>
                <div className="d-flex gap-1 ">
                  <div className='icon-wrapper'>
                    <img className="color-white-icon" src={GetImages.Signed} alt="Signature" />
                  </div>
                  <p className="signature">{trimmedName}</p>
                </div>
                {formData?.signedDateTime != null && (
                  <div className="">
                      <p className="normal_text black w-100">
                        Date Signed : {DateHelper.formatISOToMMDDYYYYTime(signedDateTime)}
                      </p>
                    
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureCard;
